import { 
  collection, 
  getDocs, 
  query, 
  where, 
  addDoc, 
  updateDoc, 
  doc, 
  serverTimestamp,
  orderBy,
  limit,
  getCountFromServer
} from 'firebase/firestore';
import { db } from './firebase';

const CUSTOMERS_COLLECTION = 'customers';

export const userService = {
  // Create a new customer profile
  async createCustomer(userData) {
    try {
      const customersRef = collection(db, CUSTOMERS_COLLECTION);
      const docRef = await addDoc(customersRef, {
        ...userData,
        totalScratches: 0,
        lastScanAt: serverTimestamp(),
        createdAt: serverTimestamp()
      });
      return { id: docRef.id, ...userData };
    } catch (error) {
      console.error("Error creating customer:", error);
      throw error;
    }
  },

  // Get customer by phone number
  async getCustomerByPhone(phone) {
    try {
      const q = query(collection(db, CUSTOMERS_COLLECTION), where('phone', '==', phone));
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        return { id: doc.id, ...doc.data() };
      }
      return null;
    } catch (error) {
      console.error("Error getting customer:", error);
      throw error;
    }
  },

  // Get all customers (Super Admin)
  async getAllUsers() {
    try {
      const querySnapshot = await getDocs(collection(db, CUSTOMERS_COLLECTION));
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error("Error getting users:", error);
      throw error;
    }
  },

  // Get total user count
  async getTotalUsers() {
    try {
      const snapshot = await getCountFromServer(collection(db, CUSTOMERS_COLLECTION));
      return snapshot.data().count;
    } catch (error) {
      console.error("Error getting total users:", error);
      return 0;
    }
  },

  // Get total platform scratches
  async getTotalScratches() {
    try {
      const snapshot = await getCountFromServer(collection(db, 'scratches'));
      return snapshot.data().count;
    } catch (error) {
      console.error("Error getting total scratches:", error);
      return 0;
    }
  },

  // Save a scratch interaction
  async saveScratch(scratchData) {
    try {
      const scratchesRef = collection(db, 'scratches');
      await addDoc(scratchesRef, {
        ...scratchData,
        timestamp: serverTimestamp()
      });
      
      // Also update total scratches count in user profile
      const user = await this.getCustomerByPhone(scratchData.userPhone);
      if (user) {
        const userRef = doc(db, CUSTOMERS_COLLECTION, user.id);
        await updateDoc(userRef, {
          totalScratches: (user.totalScratches || 0) + 1,
          lastScanAt: serverTimestamp()
        });
      }
    } catch (error) {
      console.error("Error saving scratch:", error);
    }
  },

  // Get user's scratch history
  async getUserScratches(phone) {
    try {
      const q = query(
        collection(db, 'scratches'),
        where('userPhone', '==', phone)
      );
      const querySnapshot = await getDocs(q);
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort in memory to avoid index requirements
      return results.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
    } catch (error) {
      console.error("Error getting user history:", error);
      return [];
    }
  },

  // Get all scratch history for a specific shop
  async getShopScratches(shopId) {
    try {
      const q = query(
        collection(db, 'scratches'),
        where('shopId', '==', shopId)
      );
      const querySnapshot = await getDocs(q);
      const results = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Enrich with user names if missing (for legacy data)
      const enrichedResults = await Promise.all(results.map(async (scratch) => {
        if (!scratch.userName || scratch.userName === 'Anonymous' || scratch.userName === 'Unknown User') {
          const user = await this.getCustomerByPhone(scratch.userPhone);
          if (user) {
            return { ...scratch, userName: user.name };
          }
        }
        return scratch;
      }));

      // Sort in memory
      return enrichedResults.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
    } catch (error) {
      console.error("Error getting shop history:", error);
      return [];
    }
  },

  // Get unique user counts per shop for dynamic reporting
  async getUniqueUsersPerShop() {
    try {
      const querySnapshot = await getDocs(collection(db, 'scratches'));
      const counts = {};
      
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        const shopId = data.shopId;
        const phone = data.userPhone;
        
        if (!counts[shopId]) {
          counts[shopId] = new Set();
        }
        counts[shopId].add(phone);
      });

      const finalCounts = {};
      Object.keys(counts).forEach(shopId => {
        finalCounts[shopId] = counts[shopId].size;
      });
      
      return finalCounts;
    } catch (error) {
      console.error("Error getting unique user metrics:", error);
      return {};
    }
  }
};
